import { NoteWire } from '@/api/schemas/note';
import { confirm } from '@/components/ui/confirmation-dialog';
import { ScreenHeader } from '@/components/layout/screen-header';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useLikeCheckIn } from '@/features/activity/hooks/use-like-check-in';
import { useFriendActions } from '@/features/friends/hooks/use-friend-actions';
import { useFriendshipStatus } from '@/features/friends/hooks/use-friendship-status';
import { useUserFriends } from '@/features/friends/hooks/use-user-friends';
import { listsQueryOptions } from '@/api/queries/lists';
import { ListCard } from '@/features/lists/components/list-card';
import { NotesList } from '@/features/notes/components';
import { useDeleteNote, useUpdateNote } from '@/features/notes/hooks/use-note-mutations';
import { User } from '@/types';
import { useQuery } from '@tanstack/react-query';
import { router } from 'expo-router';
import React, { useCallback, useMemo, useState } from 'react';
import { ActivityIndicator, Pressable, Text, View } from 'react-native';
import { FriendsList } from './components/friends-list';
import { ProfileActions } from './components/profile-actions';
import { ProfileFeed } from './components/profile-feed';
import { ProfileHeader } from './components/profile-header';
import { ProfileStats } from './components/profile-stats';
import { useProfileFeed } from './hooks/use-profile-feed';

interface ProfileScreenProps {
  user: User;
  isCurrentUser?: boolean;
}

export function ProfileScreen({ user, isCurrentUser = false }: ProfileScreenProps) {
  const {
    data,
    isLoading: feedLoading,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useProfileFeed(user.id);

  const { toggleLike } = useLikeCheckIn();
  const { data: friendsData, isLoading: friendsLoading } = useUserFriends(user.id);

  const { data: friendshipData } = useFriendshipStatus(user.id, {
    enabled: !isCurrentUser,
  });
  const { removeFriend } = useFriendActions();
  const deleteNoteMutation = useDeleteNote();
  const updateNoteMutation = useUpdateNote();
  const { data: listsData, isLoading: listsLoading } = useQuery({
    ...listsQueryOptions({ per_page: 20 }),
    enabled: isCurrentUser,
  });
  const userLists = listsData?.items ?? [];

  const [isHovered, setIsHovered] = useState(false);
  const isFriend = friendshipData?.status === 'friends';

  const handleDeleteNote = useCallback(async (note: NoteWire) => {
    const confirmed = await confirm({
      title: 'Delete this note?',
      description: 'Are you sure you want to delete this note? This cannot be undone.',
      variant: 'destructive',
      confirmLabel: 'Delete',
    });
    if (confirmed) {
      deleteNoteMutation.mutate(note.id);
    }
  }, [deleteNoteMutation]);

  const handleSaveNote = useCallback((noteId: string, content: string) => {
    updateNoteMutation.mutate({ id: noteId, data: { content } });
  }, [updateNoteMutation]);

  const handleRemoveFriend = async () => {
    const confirmed = await confirm({
      title: 'Remove friend?',
      description: 'Are you sure you want to remove this friend?',
      variant: 'destructive',
      confirmLabel: 'Remove',
    });
    if (confirmed) {
      removeFriend.mutate(user.id);
    }
  };

  const friendshipBadge = (!isCurrentUser && isFriend) ? (
    <Pressable
      onPress={handleRemoveFriend}
      disabled={removeFriend.isPending}
      onHoverIn={() => setIsHovered(true)}
      onHoverOut={() => setIsHovered(false)}
      className={`rounded-sm px-2 py-0.5 border ${
        isHovered ? 'bg-error/[0.09] border-error' : 'bg-surface-secondary border-border'
      }`}
      style={{
        opacity: removeFriend.isPending ? 0.5 : 1,
      }}
    >
      <Text className={`text-xs font-semibold ${isHovered ? 'text-error' : 'text-muted-foreground'}`}>
        {isHovered ? 'Unfriend' : 'Friends'}
      </Text>
    </Pressable>
  ) : undefined;

  const items = useMemo(
    () => data?.pages.flatMap((page) => page.items) ?? [],
    [data],
  );

  const checkInCount = data?.pages?.[0]?.total ?? 0;
  const friendsCount = friendsData?.length ?? 0;

  return (
    <View className="flex-1">
      {isCurrentUser && (
        <ScreenHeader
          title="Profile"
          showMenuButton
          rightSlot={
            <View className="flex-row items-center" style={{ gap: 12 }}>
              <Pressable
                onPress={() => router.push({ pathname: '/(tabs)/profile/edit' })}
              >
                <Text className="font-medium text-primary">
                  Edit Profile
                </Text>
              </Pressable>
            </View>
          }
        />
      )}
      <View className="flex-1 p-6">
        <ProfileHeader user={user} friendshipBadge={friendshipBadge} />
        <ProfileStats checkInCount={checkInCount} friendsCount={friendsCount} />
        <ProfileActions isCurrentUser={isCurrentUser} userId={user.id} />
        <Tabs defaultValue="activity" className="flex-1">
          <TabsList>
            <TabsTrigger value="activity" title="Activity" />
            <TabsTrigger value="friends" title="Friends" />
            {isCurrentUser && (
              <TabsTrigger value="lists" title="Lists" />
            )}
            {isCurrentUser && (
              <TabsTrigger value="notes" title="Notes" />
            )}
          </TabsList>
          <TabsContent value="activity">
            <ProfileFeed
              items={items}
              isLoading={feedLoading}
              hasNextPage={hasNextPage}
              isFetchingNextPage={isFetchingNextPage}
              onLoadMore={() => fetchNextPage()}
              onLike={toggleLike}
            />
          </TabsContent>
          <TabsContent value="friends">
            <FriendsList
              friends={friendsData ?? []}
              isLoading={friendsLoading}
            />
          </TabsContent>
          {isCurrentUser && (
            <TabsContent value="lists">
              {listsLoading ? (
                <View className="py-8 items-center">
                  <ActivityIndicator />
                </View>
              ) : userLists.length === 0 ? (
                <View className="items-center py-8 px-4">
                  <Text className="text-muted-foreground text-center">
                    No lists yet. Save menu items and restaurants to organize them.
                  </Text>
                  <Pressable
                    onPress={() => router.push('/(root)/lists' as any)}
                    className="mt-3"
                  >
                    <Text className="text-primary font-medium">Go to Lists</Text>
                  </Pressable>
                </View>
              ) : (
                <View style={{ paddingTop: 8 }}>
                  {userLists.map((list) => (
                    <ListCard
                      key={list.id}
                      list={list}
                      onPress={() =>
                        router.push({
                          pathname: '/(root)/lists/[id]',
                          params: { id: list.id },
                        })
                      }
                    />
                  ))}
                  <Pressable
                    onPress={() => router.push('/(root)/lists' as any)}
                    className="items-center py-3"
                  >
                    <Text className="text-sm font-medium text-primary">
                      View all lists
                    </Text>
                  </Pressable>
                </View>
              )}
            </TabsContent>
          )}
          {isCurrentUser && (
            <TabsContent value="notes">
              <NotesList
                userId={user.id}
                onSave={handleSaveNote}
                onDelete={handleDeleteNote}
                nested
              />
            </TabsContent>
          )}
        </Tabs>
      </View>
    </View>
  );
}
